import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { gibberizeCatalog } from './gibberish';

type LocaleData = Record<string, string | Record<string, any>>;

type I18nContextValue = {
  locale: string;
  setLocale: (l: string) => void;
  t: (key: string, fallback?: string | null, vars?: Record<string, any>) => string;
  register: (locale: string, data: LocaleData) => void;
};

const I18nContext = createContext<I18nContextValue | null>(null);

const flatten = (obj: LocaleData, prefix = ''): Record<string, string> => {
  const out: Record<string, string> = {};
  Object.keys(obj).forEach(k => {
    const val = (obj as any)[k];
    const key = prefix ? `${prefix}.${k}` : k;
    if (val && typeof val === 'object') {
      Object.assign(out, flatten(val as LocaleData, key));
    } else if (typeof val === 'string') {
      out[key] = val;
    }
  });
  return out;
};

export const I18nProvider: React.FC<{ defaultLocale?: string; children: React.ReactNode }> = ({ defaultLocale = 'es', children }) => {
  const getInitial = () => {
    try {
      const stored = typeof window !== 'undefined' ? window.localStorage.getItem('locale') : null;
      return stored || defaultLocale;
    } catch (e) {
      return defaultLocale;
    }
  };

  const [locale, setLocale] = useState<string>(getInitial);
  const [catalogs, setCatalogs] = useState<Record<string, Record<string, string>>>({});

  useEffect(() => {
    // try to load built-in locale files dynamically so the provider has
    // translations available immediately (useful when LanguageSwitcher
    // isn't mounted). We register both English and Spanish bundles.
    (async () => {
      try {
        const en = await import('./locales/en.json');
        register('en', en);
        
        // In development mode, generate gibberish locale from English
        if (import.meta.env.DEV || import.meta.env.MODE === 'development') {
          const enFlat = flatten(en);
          const gibberishCatalog = gibberizeCatalog(enFlat);
          // Register gibberish as a fake locale
          setCatalogs(prev => ({ ...prev, 'xx': gibberishCatalog }));
        }
      } catch (e) {
        // noop if en not available
      }
      try {
        const es = await import('./locales/es.json');
        register('es', es);
      } catch (e) {
        // noop if es not available
      }
    })();
  }, []);

  const register = useCallback((l: string, data: LocaleData) => {
    const flat = flatten(data);
    setCatalogs(prev => {
      const existing = prev[l];
      // Avoid creating a new object if the catalog is identical — prevents unnecessary re-renders
      try {
        if (existing && JSON.stringify(existing) === JSON.stringify(flat)) {
          return prev;
        }
      } catch (e) {
        // fallback to always updating if comparison fails
      }
      return { ...prev, [l]: flat };
    });
  }, []);

  const t = (key: string, fallback: string | null = null, vars?: Record<string, any>) => {
    const cat = catalogs[locale] || {};
    let str: string | undefined = undefined;
    if (key in cat) str = cat[key];
    // fallback to default locale
    if (str === undefined && locale !== 'en' && catalogs['en'] && key in catalogs['en']) str = catalogs['en'][key];
    if (str === undefined) str = fallback ?? key;

    // simple interpolation using {{key}} placeholders
    if (vars && typeof str === 'string') {
      Object.keys(vars).forEach((k) => {
        const v = vars[k];
        const re = new RegExp(`{{\\s*${k}\\s*}}`, 'g');
        if (typeof str === 'string') str = str.replace(re, String(v));
      });
    }
    return str as string;
  };

  const value = useMemo(() => ({ locale, setLocale, t, register }), [locale, catalogs, register]);

  useEffect(() => {
    // bind module-level helper for convenience in non-hook places
    try { bindContext(value); } catch (e) { /* noop */ }
    return () => { try { unbindContext(); } catch (e) { /* noop */ } };
  }, [value]);

  useEffect(() => {
    try {
      if (typeof window !== 'undefined') window.localStorage.setItem('locale', locale);
    } catch (e) {
      // ignore write errors
    }
  }, [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useTranslation = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useTranslation must be used within I18nProvider');
  return { t: ctx.t, locale: ctx.locale, setLocale: ctx.setLocale, register: ctx.register };
};

// convenience global t that reads from the current context via a tiny module-level cache
let _lastCtx: I18nContextValue | null = null;
export const bindContext = (ctx: I18nContextValue) => { _lastCtx = ctx; };
export const unbindContext = () => { _lastCtx = null; };
export const t = (key: string, fallback: string | null = null, vars?: Record<string, any>) => {
  try {
    if (_lastCtx) return _lastCtx.t(key, fallback, vars);
  } catch (e) {
    // noop
  }
  if (vars && typeof fallback === 'string') {
    let s = fallback;
    Object.keys(vars).forEach((k) => {
      s = s.replace(new RegExp(`{{\\s*${k}\\s*}}`, 'g'), String(vars[k]));
    });
    return s;
  }
  return fallback ?? key;
};

export default I18nContext;
